import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PasswordStrengthIndicator from '../../../components/PasswordReset/PasswordStrengthIndicator';
import { validatePassword } from '../../../utils/passwordValidation';
import apiClient from '../../../apiClient';
import './ResetPasswordPage.css';

function ResetPasswordPage() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [error, setError] = useState('');
  const [passwordErrors, setPasswordErrors] = useState([]);
  const [tokenValid, setTokenValid] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [userName, setUserName] = useState('');

  const validateToken = useCallback(async () => {
    try {
      const response = await apiClient.get(`/api/password/validate-token/${token}`);
      if (response.data.success) {
        setTokenValid(true);
        setUserEmail(response.data.email);
        setUserName(response.data.name);
      } else {
        setError('This password reset link is invalid or has expired.');
        setTokenValid(false);
      }
    } catch (err) {
      setError('This password reset link is invalid or has expired.');
      setTokenValid(false);
    } finally {
      setIsValidating(false);
    }
  }, [token]);

  useEffect(() => {
    validateToken();
  }, [validateToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    const validation = validatePassword(password, userEmail);
    if (!validation.isValid) {
      setError('Please fix the password requirement issues above.');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await apiClient.post('/api/password/reset-password', {
        token,
        password,
        confirmPassword
      });

      if (response.data.success) {
        navigate('/password-reset-success');
      } else {
        setError(response.data.message || 'Failed to reset password.');
        if (response.data.errors) {
          setPasswordErrors(response.data.errors);
        }
      }
    } catch (err) {
      if (err.response?.status === 400) {
        setError(err.response.data.message || 'Invalid request.');
        if (err.response.data.errors) {
          setPasswordErrors(err.response.data.errors);
        }
      } else {
        setError('An error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isValidating) {
    return (
      <div className="reset-password-page">
        <div className="reset-password-container">
          <div className="loading-spinner">Validating reset link...</div>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="reset-password-page">
        <div className="reset-password-container">
          <div className="error-container">
            <div className="error-icon">⚠️</div>
            <h2>Invalid or Expired Link</h2>
            <p>{error}</p>
            <button
              onClick={() => navigate('/forgot-password')}
              className="request-new-link"
            >
              Request New Reset Link
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="reset-password-page">
      <div className="reset-password-container">
        <div className="reset-password-header">
          <h2>Create New Password</h2>
          <p>Hi {userName}, please create a strong password for your account.</p>
          <p className="email-info">Account: {userEmail}</p>
        </div>

        <form onSubmit={handleSubmit} className="reset-password-form">
          <div className="form-group">
            <label htmlFor="password">New Password</label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter new password"
                required
                autoComplete="new-password"
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
          </div>

          <PasswordStrengthIndicator
            password={password}
            userEmail={userEmail}
          />

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <div className="password-input-wrapper">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                required
                autoComplete="new-password"
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
            {confirmPassword && password !== confirmPassword && (
              <div className="field-error">Passwords do not match</div>
            )}
          </div>

          {passwordErrors.length > 0 && (
            <div className="password-requirements">
              <h4>Password Requirements:</h4>
              <ul>
                {passwordErrors.map((err, index) => (
                  <li key={index} className="requirement-error">
                    ❌ {err}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <button
            type="submit"
            className="reset-button"
            disabled={isLoading || !password || !confirmPassword}
          >
            {isLoading ? 'Resetting Password...' : 'Reset Password'}
          </button>
        </form>

        <div className="security-tips">
          <h4>💡 Password Tips:</h4>
          <ul>
            <li>Use a unique password for your work account</li>
            <li>Consider using a password manager</li>
            <li>Avoid using personal information</li>
            <li>Mix uppercase, lowercase, numbers, and symbols</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default ResetPasswordPage;